import { useEffect, useRef } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from './ui/dialog';

type CalendlyModalProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  url: string;
  title?: string;
  /** Feature 104: tier-aware copy. */
  description?: string;
  /**
   * Feature 137: called with the scheduled-event URI when the invitee completes a booking. Only
   * then is the embed asked to report bookings — Calendly posts nothing without `embed_domain`.
   */
  onEventScheduled?: (eventUri: string) => void;
};

const CALENDLY_ORIGIN = 'https://calendly.com';

type CalendlyMessage = { event?: unknown; payload?: { event?: { uri?: unknown } } } | null;

/**
 * Calendly's documented embed API posts objects, but older embeds post the same shape as a JSON
 * string — accept both, and treat anything unparseable as not a booking.
 */
const parseCalendlyMessage = (data: unknown): CalendlyMessage => {
  if (typeof data !== 'string') return data as CalendlyMessage;
  try {
    return JSON.parse(data) as CalendlyMessage;
  } catch {
    return null;
  }
};

const withEmbedDomain = (url: string): string => {
  const embedUrl = new URL(url);
  embedUrl.searchParams.set('embed_domain', window.location.host);
  embedUrl.searchParams.set('embed_type', 'Inline');
  return embedUrl.toString();
};

/**
 * Calendly Modal Component
 *
 * Embeds a Calendly scheduling widget inside a modal dialog using an iframe.
 * Simple and reliable approach that works across all browsers.
 *
 * @param open - Controls modal visibility
 * @param onOpenChange - Callback when modal open state changes
 * @param url - Calendly scheduling page URL
 * @param title - Optional modal title
 * @param onEventScheduled - Optional booking callback (see the prop's doc)
 */
export function CalendlyModal({
  open,
  onOpenChange,
  url,
  title = 'Schedule a time',
  description = 'Select a time that works for you',
  onEventScheduled,
}: CalendlyModalProps) {
  // A ref, so a caller passing a fresh arrow each render does not re-subscribe the listener.
  const onEventScheduledRef = useRef(onEventScheduled);
  onEventScheduledRef.current = onEventScheduled;
  const reportsBookings = Boolean(onEventScheduled);

  useEffect(() => {
    if (!open || !reportsBookings) return;

    // react-calendly's useCalendlyEventListener does not check the sender, so any frame on the
    // page could fake a booking; this listener only trusts Calendly's own origin.
    const handleMessage = (e: MessageEvent) => {
      if (e.origin !== CALENDLY_ORIGIN) return;
      const data = parseCalendlyMessage(e.data);
      const eventUri = data?.payload?.event?.uri;
      if (data?.event === 'calendly.event_scheduled' && typeof eventUri === 'string') {
        onEventScheduledRef.current?.(eventUri);
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [open, reportsBookings]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl h-[90vh] p-0 flex flex-col overflow-hidden">
        <DialogHeader className="px-6 pt-6 pb-4 flex-shrink-0">
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>
        <div className="flex-1 px-6 pb-6 overflow-auto">
          <iframe
            src={reportsBookings && url ? withEmbedDomain(url) : url}
            width="100%"
            height="100%"
            frameBorder="0"
            title="Calendly Scheduling"
            className="rounded-lg min-h-[500px] h-full"
            sandbox="allow-scripts allow-same-origin allow-forms allow-popups allow-popups-to-escape-sandbox"
            referrerPolicy="strict-origin-when-cross-origin"
          />
        </div>
      </DialogContent>
    </Dialog>
  );
}
